import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin, Clock, X } from 'lucide-react';
import { getListingUrl } from '../utils/slugify';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SearchAutocomplete = ({
  placeholder = "Search businesses, events, jobs...",
  listingType,
  onSearch,
  onChange,        // called on every keystroke so parent can sync inputValue
  initialValue = '', // shows current URL search param in the input on page load
  className = "",
  inputClassName = "",
}) => {
  const [query, setQuery] = useState(initialValue || '');
  const [suggestions, setSuggestions] = useState({ primary: [], primary_type: null, other: [] });
  const [showDropdown, setShowDropdown] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const navigate = useNavigate();
  const wrapperRef = useRef(null);

  // Keep input in sync when the URL param changes (e.g. "Clear Filters" or direct link)
  useEffect(() => {
    setQuery(initialValue || '');
  }, [initialValue]);

  // Close dropdown when tapping outside the entire search component,
  // but NOT when the keyboard dismisses (which only fires blur, not an outside tap).
  useEffect(() => {
    const handleOutsideInteraction = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideInteraction);
    document.addEventListener('touchstart', handleOutsideInteraction);
    return () => {
      document.removeEventListener('mousedown', handleOutsideInteraction);
      document.removeEventListener('touchstart', handleOutsideInteraction);
    };
  }, []);

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('de_recent_searches') || '[]');
      setRecentSearches(stored);
    } catch { /* ignore */ }
  }, []);

  const fetchSuggestions = useCallback(async (q) => {
    if (q.length < 2) { setSuggestions({ primary: [], primary_type: null, other: [] }); return; }
    const cacheKey = `ac_${listingType || 'all'}_${q.toLowerCase().trim()}`;
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      try {
        const { data, ts } = JSON.parse(cached);
        if (Date.now() - ts < 300000) { setSuggestions(data); setShowDropdown(true); return; }
      } catch { /* ignore */ }
    }
    try {
      const params = new URLSearchParams({ q });
      if (listingType) params.append('type', listingType);
      const res = await axios.get(`${API}/listings/search/suggestions?${params}`);
      // Normalise both response shapes (legacy flat array + new structured object).
      let normalised;
      if (Array.isArray(res.data)) {
        normalised = { primary: res.data, primary_type: listingType || null, other: [] };
      } else {
        normalised = {
          primary: Array.isArray(res.data?.primary) ? res.data.primary : [],
          primary_type: res.data?.primary_type || listingType || null,
          other: Array.isArray(res.data?.other) ? res.data.other : [],
        };
      }
      setSuggestions(normalised);
      setShowDropdown(true);
      try { sessionStorage.setItem(cacheKey, JSON.stringify({ data: normalised, ts: Date.now() })); } catch { /* ignore */ }
    } catch { /* ignore */ }
  }, [listingType]);

  useEffect(() => {
    const t = setTimeout(() => fetchSuggestions(query), 300);
    return () => clearTimeout(t);
  }, [query, fetchSuggestions]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setShowDropdown(false);
    const stored = JSON.parse(localStorage.getItem('de_recent_searches') || '[]');
    const updated = [query.trim(), ...stored.filter(s => s.toLowerCase() !== query.trim().toLowerCase())].slice(0, 5);
    localStorage.setItem('de_recent_searches', JSON.stringify(updated));
    if (onSearch) { onSearch(query.trim()); }
    else { navigate(`/search?q=${encodeURIComponent(query.trim())}${listingType ? `&type=${listingType}` : ''}`); }
  };

  const handleSuggestionClick = (listing) => {
    setShowDropdown(false);
    setQuery('');
    navigate(getListingUrl(listing));
  };

  const typeLabel = (t) => t === 'classified' ? 'Marketplace' : t?.charAt(0).toUpperCase() + t?.slice(1);
  const typePlural = (t) => {
    if (!t) return 'results';
    if (t === 'classified') return 'marketplace listings';
    if (t === 'business') return 'businesses';
    return `${t}s`;
  };

  const renderSuggestionRow = (s) => (
    <button
      key={s.id}
      onMouseDown={() => handleSuggestionClick(s)}
      className="w-full px-4 py-2.5 hover:bg-slate-50 dark:hover:bg-slate-700 text-left flex items-center gap-3 transition-colors"
      data-testid={`suggestion-${s.id}`}
    >
      <MapPin className="h-3.5 w-3.5 text-[#4A6741] shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm text-slate-800 dark:text-slate-200 truncate">{s.title}</div>
        <div className="text-xs text-slate-500 dark:text-slate-400">{s.is_outside_essex && s.region && s.region !== 'Essex' ? s.region : s.town} &bull; {s.category}</div>
      </div>
      <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-600 text-slate-500 dark:text-slate-300 capitalize shrink-0">{typeLabel(s.listing_type)}</span>
    </button>
  );

  const hasAnyResults = suggestions.primary.length > 0 || suggestions.other.length > 0;
  const showCrossCategoryHint = (
    listingType &&
    query.length >= 2 &&
    suggestions.primary.length === 0 &&
    suggestions.other.length > 0
  );

  return (
    <form onSubmit={handleSubmit} className={`relative ${className}`} data-testid="search-autocomplete" ref={wrapperRef}>
      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); if (onChange) onChange(e.target.value); }}
          onFocus={() => setShowDropdown(true)}
          placeholder={placeholder}
          className={`w-full pl-10 pr-10 py-2.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-[#D4A017]/30 focus:border-[#D4A017] transition-all text-[16px] ${inputClassName}`}
          data-testid="search-autocomplete-input"
        />
        {query && (
          <button type="button" onClick={() => { setQuery(''); if (onChange) onChange(''); setSuggestions({ primary: [], primary_type: null, other: [] }); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {showDropdown && hasAnyResults && (
        <div className="absolute top-full left-0 right-0 mt-1.5 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 z-50 overflow-hidden max-h-80 overflow-y-auto" data-testid="search-suggestions-dropdown">
          {/* Friendly "0 found in this category" hint when on a category page
              and the query has no in-category matches but does match other types. */}
          {showCrossCategoryHint && (
            <div
              className="px-4 py-2.5 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-100 dark:border-amber-800 text-xs text-amber-800 dark:text-amber-200"
              data-testid="search-cross-category-hint"
            >
              No {typePlural(listingType)} match <span className="font-semibold">"{query}"</span> — but we found matches across other categories below.
            </div>
          )}

          {/* Primary section — same listing type as the page we're on. */}
          {suggestions.primary.length > 0 && (
            <>
              {listingType && (
                <div className="px-4 pt-2 pb-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wide bg-slate-50 dark:bg-slate-900/40">
                  {typeLabel(listingType)} matches
                </div>
              )}
              {suggestions.primary.map(renderSuggestionRow)}
            </>
          )}

          {/* Other section — cross-category matches, only shown when listingType is set. */}
          {listingType && suggestions.other.length > 0 && (
            <>
              <div className="px-4 pt-2 pb-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wide bg-slate-50 dark:bg-slate-900/40 border-t border-slate-100 dark:border-slate-700" data-testid="search-other-categories-header">
                Also matched in other categories
              </div>
              {suggestions.other.map(renderSuggestionRow)}
            </>
          )}
        </div>
      )}

      {showDropdown && !hasAnyResults && query.length >= 2 && !recentSearches.length && (
        <div className="absolute top-full left-0 right-0 mt-1.5 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 z-50 overflow-hidden" data-testid="search-no-results-dropdown">
          <div className="px-4 py-3 text-xs text-slate-500 dark:text-slate-400">
            No results match <span className="font-semibold">"{query}"</span>{listingType ? ` in ${typePlural(listingType)} or other categories` : ''}.
          </div>
        </div>
      )}

      {showDropdown && !hasAnyResults && !query && recentSearches.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1.5 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 z-50 overflow-hidden" data-testid="recent-searches-dropdown">
          <div className="px-4 py-2 flex items-center justify-between border-b border-slate-100 dark:border-slate-700">
            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Recent searches</span>
            <button onMouseDown={(e) => { e.preventDefault(); localStorage.removeItem('de_recent_searches'); setRecentSearches([]); setShowDropdown(false); }} className="text-[10px] text-slate-400 hover:text-red-500">Clear</button>
          </div>
          {recentSearches.map((term, i) => (
            <button
              key={i}
              onMouseDown={() => { setQuery(term); if (onSearch) onSearch(term); else navigate(`/search?q=${encodeURIComponent(term)}`); setShowDropdown(false); }}
              className="w-full px-4 py-2.5 hover:bg-slate-50 dark:hover:bg-slate-700 text-left flex items-center gap-3 transition-colors"
            >
              <Clock className="h-3.5 w-3.5 text-slate-400" />
              <span className="text-sm text-slate-700 dark:text-slate-300">{term}</span>
            </button>
          ))}
        </div>
      )}
    </form>
  );
};

export default SearchAutocomplete;
